import {useId} from "react";
import BaseButton from "components/BaseButton";
import BaseDialog from "components/BaseDialog";
import {DialogBody} from "components/ClosableDialog.styles";
import {useMessages} from "i18n";
import {BrowserTabIcon} from "./icons";
import {Actions, BodyText, DontShowAgain, DontShowAgainBox, IconTile, TitleRow, TitleText} from "./KeepTabOpenDialog.styles";

interface Props {
	open: boolean;
	dontShowAgain: boolean;
	onDontShowAgainChange: (checked: boolean) => void;
	/** The button, Escape and the scrim alike. */
	onClose: () => void;
	/** Runs once the dialog has finished closing, to hand focus back to the card under it. */
	onExited: () => void;
}

/**
 * The keep-this-tab-open notice over a direct transfer's live card (kitecorp/transferify-ui#3724): it says, before
 * the sender shares the link, that the file goes from this tab and stops when the tab does. Informative: one button,
 * Escape and the scrim close it too, and closing it leaves the card exactly as it was. When it opens and what its
 * "Don't show this again" box records are `./keepTabOpenNotice`'s rules, held by `./useKeepTabOpenNotice`.
 *
 * Focus starts on the button. The card owns where it goes back to: nothing focusable opened this dialog (the send
 * button that led to the card is gone with the composer), so MUI's own restore would drop focus on the page, and the
 * card restores it itself once the transition has finished.
 *
 * The box is a plain `<input type="checkbox">` inside its `<label>`, as `marketing/MarketingObjection` draws its own:
 * the label text is its accessible name and the whole row is the hit target, and MUI's checkbox stays out of the
 * landing page's chunk, which renders this card.
 */
export function KeepTabOpenDialog({open, dontShowAgain, onDontShowAgainChange, onClose, onExited}: Props) {
	const copy = useMessages().sender.instant.keepTabOpen;
	const bodyId = useId();
	return (
		<BaseDialog
			open={open}
			onClose={onClose}
			title={(
				<TitleRow>
					<IconTile><BrowserTabIcon/></IconTile>
					<TitleText>{copy.title}</TitleText>
				</TitleRow>
			)}
			aria-describedby={bodyId}
			disableRestoreFocus
			onTransitionExited={onExited}
		>
			<DialogBody>
				<BodyText id={bodyId}>{copy.body}</BodyText>
				<Actions>
					<DontShowAgain>
						<DontShowAgainBox
							type="checkbox"
							checked={dontShowAgain}
							onChange={event => onDontShowAgainChange(event.target.checked)}
						/>
						{copy.dontShowAgain}
					</DontShowAgain>
					<BaseButton type="button" size="compact" autoFocus onClick={onClose}>{copy.confirm}</BaseButton>
				</Actions>
			</DialogBody>
		</BaseDialog>
	);
}
